import { useEncryptionContext } from "../../contexts/EncryptionContext";
import { useFamilyStore } from "../../stores/familyStore";

/**
 * Pełnoekranowy modal wyświetlany gdy:
 *   - rodzina ma już aktywowane E2EE (encryption_active_since != null),
 *   - ALE bieżące urządzenie nie ma jeszcze klucza rodzinnego
 *     (np. pierwsze logowanie na nowej przeglądarce / nowym urządzeniu).
 *
 * Klucz przyjdzie automatycznie gdy inne urządzenie tego usera lub partnera
 * będzie online — `useEncryption` nasłuchuje reaktywnie i przekaże klucz przez
 * ECDH wrap. Modal zniknie sam gdy `isReady` przejdzie na true.
 *
 * Dopóki modal jest widoczny, próba zapisu danych zostanie zablokowana przez
 * `maybeEncryptForFamily` (rzuca E2EENotReadyError).
 */
export function DeviceSyncBanner() {
  const { family } = useFamilyStore();
  const { isReady } = useEncryptionContext();

  if (!family) return null;
  if (!family.encryption_active_since) return null; // bypass mode — banner not needed
  if (isReady) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="text-center mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg
              className="w-6 h-6 text-blue-600 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900">
            Synchronizuję klucze szyfrowania
          </h2>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed">
          To urządzenie jeszcze nie ma klucza do twoich zaszyfrowanych danych.
        </p>

        <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
          <li>
            Otwórz aplikację na innym swoim urządzeniu (np. telefon lub laptop), na którym już
            byłaś/byłeś zalogowana/y.
          </li>
          <li>
            Albo poproś partnera, żeby otworzył 2homes — jego urządzenie automatycznie
            przekaże klucz.
          </li>
        </ul>

        <p className="mt-4 text-xs text-slate-400">
          Synchronizacja zwykle trwa kilka sekund. Możesz zostawić to okno otwarte —
          zniknie samo gdy klucz dotrze.
        </p>
      </div>
    </div>
  );
}
